import { useEffect, useState, useCallback, useRef } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getMeetings, createMeeting, updateMeeting, deleteMeeting,
  setRsvp, uploadMeetingAttachment, deleteMeetingAttachment,
} from '../services/ApiService';
import type { Meeting, Protocol } from '../types/models';
import Modal from './Modal';
import MeetingAttachments from './MeetingAttachments';
import MeetingProtocol from './MeetingProtocol';
import {
  Plus, CalendarDays, MapPin, Clock, Check, X as XIcon,
  HelpCircle, Trash2, Pencil, Paperclip, X, Loader2,
  ArrowLeft, FileText,
} from 'lucide-react';
import { AnalyticsEvents, track } from '../services/AnalyticsService';

export default function MeetingsTab() {
  const { currentOrg, isBoardMember, updateLastSeen } = useOrganization();
  const { user } = useAuth();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Meeting | null>(null);
  const [form, setForm] = useState<MeetingFormState>({ title: '', description: '', location: '', startsAt: '', notificationChannel: 'sms' });
  const [saving, setSaving] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);

  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<{ id: string; fileName: string }[]>([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchMeetings = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const data = await getMeetings(currentOrg.id);
      setMeetings(data);
      if (selectedMeeting) {
        const updated = data.find(m => m.id === selectedMeeting.id);
        if (updated) setSelectedMeeting(updated);
      }
    } catch {
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [currentOrg, selectedMeeting]);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);
  useEffect(() => { if (currentOrg) updateLastSeen('meetings'); }, [currentOrg, updateLastSeen]);

  const resetAttachmentState = () => {
    setAttachmentFiles([]);
    setExistingAttachments([]);
    setRemovedAttachmentIds([]);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', description: '', location: '', startsAt: '', notificationChannel: 'sms' });
    resetAttachmentState();
    setShowModal(true);
  };

  const openEdit = (m: Meeting) => {
    setEditing(m);
    setForm({
      title: m.title,
      description: m.description,
      location: m.location,
      startsAt: m.meetingDate.slice(0, 16),
      notificationChannel: m.notificationChannel,
    });
    setAttachmentFiles([]);
    setExistingAttachments(
      (m.attachments ?? []).map(a => ({ id: a.id, fileName: a.fileName }))
    );
    setRemovedAttachmentIds([]);
    setShowModal(true);
  };

  const handleAttachmentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setAttachmentFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeNewAttachment = (idx: number) => {
    setAttachmentFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const removeExistingAttachment = (id: string) => {
    setExistingAttachments(prev => prev.filter(a => a.id !== id));
    setRemovedAttachmentIds(prev => [...prev, id]);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !user) return;
    setSaving(true);

    try {
      const basePayload = {
        title: form.title,
        description: form.description,
        location: form.location,
        meetingDate: new Date(form.startsAt).toISOString(),
      };

      let meetingId: string;

      if (editing) {
        meetingId = editing.id;
        await updateMeeting(currentOrg.id, editing.id, basePayload);

        for (const attId of removedAttachmentIds) {
          await deleteMeetingAttachment(currentOrg.id, editing.id, attId);
        }
      } else {
        const created = await createMeeting(currentOrg.id, { ...basePayload, notificationChannel: form.notificationChannel });
        meetingId = created.id;
        track(AnalyticsEvents.MeetingCreated, {
          orgId: currentOrg.id, notificationChannel: form.notificationChannel, attachmentCount: attachmentFiles.length,
        });
      }

      for (const file of attachmentFiles) {
        await uploadMeetingAttachment(currentOrg.id, meetingId, file);
      }

      setShowModal(false);
      resetAttachmentState();
      fetchMeetings();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Något gick fel. Försök igen.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!currentOrg) return;
    if (!window.confirm('Ta bort detta möte?')) return;
    try {
      await deleteMeeting(currentOrg.id, id);
      if (selectedMeeting?.id === id) setSelectedMeeting(null);
      fetchMeetings();
    } catch {
      alert('Kunde inte ta bort mötet.');
    }
  };

  const handleRsvp = async (meetingId: string, status: string) => {
    if (!user || !currentOrg) return;
    const meeting = meetings.find(m => m.id === meetingId);
    if (meeting?.myRsvp === status) {
      // Toggle off - send empty or no status; use a special value
      try {
        await setRsvp(currentOrg.id, meetingId, '');
      } catch {
        // ignore
      }
    } else {
      try {
        await setRsvp(currentOrg.id, meetingId, status);
      } catch {
        // ignore
      }
    }
    fetchMeetings();
  };

  const handleProtocolChange = (protocol: Protocol | null) => {
    if (selectedMeeting) {
      const updated = { ...selectedMeeting, protocol };
      setSelectedMeeting(updated);
      setMeetings(prev => prev.map(m => m.id === updated.id ? updated : m));
    }
  };

  const [activeTab, setActiveTab] = useState<'upcoming' | 'previous'>('upcoming');
  const isPast = (d: string) => new Date(d) < new Date();

  if (loading) return <div className="py-12 text-center text-gray-500">Laddar...</div>;

  if (selectedMeeting) {
    return (
      <MeetingDetailView
        meeting={selectedMeeting}
        orgId={currentOrg?.id ?? ''}
        user={user}
        isBoardMember={isBoardMember}
        onBack={() => setSelectedMeeting(null)}
        onEdit={openEdit}
        onDelete={handleDelete}
        onRsvp={handleRsvp}
        onProtocolChange={handleProtocolChange}
        isPast={isPast(selectedMeeting.meetingDate)}
        modal={
          <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Redigera möte">
            <MeetingForm
              form={form}
              setForm={setForm}
              saving={saving}
              onSave={handleSave}
              onCancel={() => setShowModal(false)}
              isEditing
              attachmentFiles={attachmentFiles}
              existingAttachments={existingAttachments}
              fileInputRef={fileInputRef}
              onAttachmentSelect={handleAttachmentSelect}
              onRemoveNew={removeNewAttachment}
              onRemoveExisting={removeExistingAttachment}
            />
          </Modal>
        }
      />
    );
  }

  const upcoming = meetings
    .filter(m => !isPast(m.meetingDate))
    .sort((a, b) => new Date(a.meetingDate).getTime() - new Date(b.meetingDate).getTime());
  const past = meetings
    .filter(m => isPast(m.meetingDate))
    .sort((a, b) => new Date(b.meetingDate).getTime() - new Date(a.meetingDate).getTime());

  const activeMeetings = activeTab === 'upcoming' ? upcoming : past;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        {isBoardMember && (
          <button data-tour="meeting-add" onClick={openCreate} className="flex items-center gap-2 px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium text-sm ml-auto">
            <Plus className="w-4 h-4" />
            Nytt möte
          </button>
        )}
      </div>

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1">
        <button
          onClick={() => setActiveTab('upcoming')}
          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'upcoming'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Kommande
          {upcoming.length > 0 && (
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'upcoming' ? 'bg-teal-100 text-teal-700' : 'bg-gray-200 text-gray-500'
            }`}>
              {upcoming.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('previous')}
          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'previous'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Tidigare
          {past.length > 0 && (
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'previous' ? 'bg-gray-300 text-gray-600' : 'bg-gray-200 text-gray-500'
            }`}>
              {past.length}
            </span>
          )}
        </button>
      </div>

      {activeMeetings.length === 0 ? (
        <div className="text-center py-16">
          <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">
            {activeTab === 'upcoming' ? 'Inga kommande möten' : 'Inga tidigare möten'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeMeetings.map(m => (
            <MeetingCard
              key={m.id}
              meeting={m}
              user={user}
              isBoardMember={isBoardMember}
              onEdit={openEdit}
              onDelete={handleDelete}
              onRsvp={handleRsvp}
              onOpen={() => setSelectedMeeting(m)}
              past={activeTab === 'previous'}
            />
          ))}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Redigera möte' : 'Nytt möte'}>
        <MeetingForm
          form={form}
          setForm={setForm}
          saving={saving}
          onSave={handleSave}
          onCancel={() => setShowModal(false)}
          isEditing={!!editing}
          attachmentFiles={attachmentFiles}
          existingAttachments={existingAttachments}
          fileInputRef={fileInputRef}
          onAttachmentSelect={handleAttachmentSelect}
          onRemoveNew={removeNewAttachment}
          onRemoveExisting={removeExistingAttachment}
        />
      </Modal>
    </div>
  );
}

interface MeetingFormState {
  title: string;
  description: string;
  location: string;
  startsAt: string;
  notificationChannel: 'sms' | 'email';
}

function MeetingForm({ form, setForm, saving, onSave, onCancel, isEditing, attachmentFiles, existingAttachments, fileInputRef, onAttachmentSelect, onRemoveNew, onRemoveExisting }: {
  form: MeetingFormState;
  setForm: (f: MeetingFormState) => void;
  saving: boolean;
  onSave: (e: React.FormEvent) => void;
  onCancel: () => void;
  isEditing: boolean;
  attachmentFiles: File[];
  existingAttachments: { id: string; fileName: string }[];
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onAttachmentSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveNew: (idx: number) => void;
  onRemoveExisting: (id: string) => void;
}) {
  return (
    <form onSubmit={onSave} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Titel</label>
        <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Beskrivning</label>
        <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Plats</label>
        <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Datum och tid</label>
        <input type="datetime-local" value={form.startsAt} onChange={e => setForm({ ...form, startsAt: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
      </div>
      {!isEditing && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Meddela deltagare via</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="notificationChannel"
                checked={form.notificationChannel === 'sms'}
                onChange={() => setForm({ ...form, notificationChannel: 'sms' })}
                className="text-teal-600 focus:ring-teal-500"
              />
              SMS
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="notificationChannel"
                checked={form.notificationChannel === 'email'}
                onChange={() => setForm({ ...form, notificationChannel: 'email' })}
                className="text-teal-600 focus:ring-teal-500"
              />
              E-post
            </label>
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Bilagor</label>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
        >
          <Paperclip className="w-4 h-4" />
          Lägg till filer
        </button>
        <input ref={fileInputRef} type="file" multiple onChange={onAttachmentSelect} className="hidden" />
        {(existingAttachments.length > 0 || attachmentFiles.length > 0) && (
          <div className="mt-2 space-y-1.5">
            {existingAttachments.map(att => (
              <div key={att.id} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg text-sm">
                <Paperclip className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="text-gray-700 truncate flex-1">{att.fileName}</span>
                <button type="button" onClick={() => onRemoveExisting(att.id)} className="p-0.5 rounded hover:bg-red-50 transition-colors">
                  <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                </button>
              </div>
            ))}
            {attachmentFiles.map((file, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 rounded-lg text-sm">
                <Paperclip className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                <span className="text-gray-700 truncate flex-1">{file.name}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)} KB` : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                </span>
                <button type="button" onClick={() => onRemoveNew(idx)} className="p-0.5 rounded hover:bg-red-50 transition-colors">
                  <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-600">Avbryt</button>
        <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {saving ? 'Sparar...' : isEditing ? 'Uppdatera' : 'Skapa möte'}
        </button>
      </div>
    </form>
  );
}

function MeetingCard({ meeting: m, user, isBoardMember, onEdit, onDelete, onRsvp, onOpen, past }: {
  meeting: Meeting;
  user: { id: string } | null;
  isBoardMember: boolean;
  onEdit: (m: Meeting) => void;
  onDelete: (id: string) => void;
  onRsvp: (meetingId: string, status: string) => void;
  onOpen: () => void;
  past?: boolean;
}) {
  const myRsvp = m.myRsvp;
  const attending = m.rsvps?.filter(r => r.status === 'attending').length ?? 0;
  const maybe = m.rsvps?.filter(r => r.status === 'maybe').length ?? 0;

  const formatDate = (d: string) => new Date(d).toLocaleDateString('sv-SE', { weekday: 'short', month: 'short', day: 'numeric' });
  const formatTime = (d: string) => new Date(d).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-5 transition-all ${past ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <button onClick={onOpen} className="text-left group">
          <h3 className="font-semibold text-gray-900 group-hover:text-teal-700 transition-colors">{m.title}</h3>
        </button>
        <div className="flex items-center gap-1">
          <button onClick={onOpen} className="p-1.5 rounded-lg hover:bg-gray-100" title="Öppna möte">
            <FileText className="w-4 h-4 text-gray-400" />
          </button>
          {isBoardMember && !past && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onEdit(m); }} className="p-1.5 rounded-lg hover:bg-gray-100">
                <Pencil className="w-4 h-4 text-gray-400" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); onDelete(m.id); }} className="p-1.5 rounded-lg hover:bg-red-50">
                <Trash2 className="w-4 h-4 text-gray-400" />
              </button>
            </>
          )}
        </div>
      </div>
      {m.description && <p className="text-sm text-gray-600 mb-3">{m.description}</p>}
      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
        <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{formatDate(m.meetingDate)}</span>
        <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{formatTime(m.meetingDate)}</span>
        {m.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{m.location}</span>}
      </div>
      <MeetingAttachments attachments={m.attachments ?? []} />
      <div className="flex items-center justify-between mt-4">
        <div className="text-xs text-gray-400">
          {attending} deltar{maybe > 0 && ` | ${maybe} kanske`}
        </div>
        {!past && (
          <div className="flex gap-1.5">
            {[
              { status: 'attending', icon: Check, label: 'Deltar', color: 'teal' },
              { status: 'maybe', icon: HelpCircle, label: 'Kanske', color: 'amber' },
              { status: 'not_attending', icon: XIcon, label: 'Nej', color: 'red' },
            ].map(opt => (
              <button
                key={opt.status}
                onClick={() => onRsvp(m.id, opt.status)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  myRsvp === opt.status
                    ? opt.color === 'teal' ? 'bg-teal-100 text-teal-800' : opt.color === 'amber' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                <opt.icon className="w-3.5 h-3.5" />
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MeetingDetailView({ meeting: m, orgId, user, isBoardMember, onBack, onEdit, onDelete, onRsvp, onProtocolChange, isPast, modal }: {
  meeting: Meeting;
  orgId: string;
  user: { id: string } | null;
  isBoardMember: boolean;
  onBack: () => void;
  onEdit: (m: Meeting) => void;
  onDelete: (id: string) => void;
  onRsvp: (meetingId: string, status: string) => void;
  onProtocolChange: (p: Protocol | null) => void;
  isPast: boolean;
  modal: React.ReactNode;
}) {
  const myRsvp = m.myRsvp;
  const attending = m.rsvps?.filter(r => r.status === 'attending').length ?? 0;
  const maybe = m.rsvps?.filter(r => r.status === 'maybe').length ?? 0;
  const notAttending = m.rsvps?.filter(r => r.status === 'not_attending').length ?? 0;

  const formatDate = (d: string) => new Date(d).toLocaleDateString('sv-SE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const formatTime = (d: string) => new Date(d).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Tillbaka till möten
      </button>

      <div className={`bg-white rounded-xl border border-gray-200 p-6 mb-6 ${isPast ? 'opacity-75' : ''}`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{m.title}</h1>
            {isPast && (
              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-md">
                Avslutat
              </span>
            )}
          </div>
          {isBoardMember && !isPast && (
            <div className="flex gap-1">
              <button onClick={() => onEdit(m)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <Pencil className="w-4 h-4 text-gray-400" />
              </button>
              <button onClick={() => onDelete(m.id)} className="p-2 rounded-lg hover:bg-red-50 transition-colors">
                <Trash2 className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          )}
        </div>

        {m.description && (
          <p className="text-gray-600 mb-4 leading-relaxed">{m.description}</p>
        )}

        <div className="flex flex-wrap gap-5 text-sm text-gray-600 mb-5">
          <span className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-gray-400" />
            {formatDate(m.meetingDate)}
          </span>
          <span className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            {formatTime(m.meetingDate)}
          </span>
          {m.location && (
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              {m.location}
            </span>
          )}
        </div>

        <MeetingAttachments attachments={m.attachments ?? []} />

        <div className="border-t border-gray-100 mt-5 pt-5">
          <div className="flex items-center justify-between">
            <div className="flex gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Check className="w-3.5 h-3.5 text-teal-500" />
                {attending} deltar
              </span>
              <span className="flex items-center gap-1">
                <HelpCircle className="w-3.5 h-3.5 text-amber-500" />
                {maybe} kanske
              </span>
              <span className="flex items-center gap-1">
                <XIcon className="w-3.5 h-3.5 text-red-400" />
                {notAttending} nej
              </span>
            </div>
            {!isPast && (
              <div className="flex gap-1.5">
                {[
                  { status: 'attending', icon: Check, label: 'Deltar', color: 'teal' },
                  { status: 'maybe', icon: HelpCircle, label: 'Kanske', color: 'amber' },
                  { status: 'not_attending', icon: XIcon, label: 'Nej', color: 'red' },
                ].map(opt => (
                  <button
                    key={opt.status}
                    onClick={() => onRsvp(m.id, opt.status)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      myRsvp === opt.status
                        ? opt.color === 'teal' ? 'bg-teal-100 text-teal-800' : opt.color === 'amber' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <opt.icon className="w-3.5 h-3.5" />
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Protokoll</h2>
        <MeetingProtocol
          orgId={orgId}
          meetingId={m.id}
          protocol={m.protocol ?? null}
          onProtocolChange={onProtocolChange}
        />
      </div>

      {modal}
    </div>
  );
}
